class Collection {
  constructor() {
    this.selectors = {
      section: ".facest-filters-section",
      toolbar: ".m-collection-toolbar",
      cols: [".m-toolbar--column-switcher button"],
      productContainer: "[data-product-container]",
      loadMoreBtn: "[data-load-more]",
      sidebar: ".m-sidebar",
      sidebarContent: ".m-sidebar--content",
      openSidebar: ".m-sidebar--open",
      closeSidebar: ".m-sidebar--close",
      filtersWrapper: ".m-filter--wrapper",
    };
    this.mobileSortingSelectors = {
      openSorting: ".m-sortby--open",
      closeSorting: ".m-sortby-mobile--close",
      sortingWrapper: ".m-sortby-mobile--wrapper",
      sortingList: ".m-sortby-mobile--content",
    };
    this.STORAGE_KEY = "gridColumnViews";
    this.isDesignMode = LhSettings.design_mode;
    this.activeCol = null;
    this.sideEffectEventsAdded = false;
  }

  init = () => {
    this.domNodes = queryDomNodes(this.selectors);
    this.mbSortingNodes = queryDomNodes(this.mobileSortingSelectors);
    this.setData();
    this.initGridView();
    this.initFilters();
    this.initMobileSorting();
    this.initProducts();
    this.sideEffectEventsAdded = true;
    LhEvents.subscribe("ON_PRODUCT_LIST_UPDATED", () => refreshProductReview());
    this.handleStickyToolbar();
    document.addEventListener("matchMobile", () => this.handleStickyToolbar());
    document.addEventListener("unmatchMobile", () => this.handleStickyToolbar());
  };

  isMobile() {
    return window.innerWidth < 768;
  }

  getStorageKey() {
    return `${this.STORAGE_KEY}_${this.isMobile() ? 'mobile' : 'desktop'}_${this.collectionId}`;
  }

  getDefaultCol() {
    if (this.isMobile()) {
      return Number(this.domNodes.section.dataset.mobileDefaultColumn) || 1;
    }
    return this.initialCol;
  }

  setData = () => {
    const { section, productContainer } = this.domNodes;
    this.sectionId = section.dataset.sectionId;
    this.paginationType = section.dataset.paginationType;
    this.totalPages = productContainer && Number(productContainer.dataset.totalPages);
    this.view = section.dataset.view;
    this.collectionId = section.dataset.collectionId || 'default';
    this.initialCol = Number(section.dataset.initialColumn);
    this.showColSwitchers = section.dataset.showColSwitchers === "true";
    this.enableFilters = section.dataset.enableFilters === "true";
    this.enableSorting = section.dataset.enableSorting === "true";
    this.filtersType = section.dataset.filtersType;
    this.filtersPosition = section.dataset.filtersPosition;
    this.currentPage = 1;
    this.activeCol = null;
  };

  initGridView = () => {
    const storageKey = this.getStorageKey();
    const cachedCol = Number(window.localStorage.getItem(storageKey));
    let colToUse;

    if (this.isMobile()) {
      colToUse = Math.min(Math.max(cachedCol || this.getDefaultCol(), 1), 2);
    } else {
      colToUse = this.isDesignMode ? this.getDefaultCol() : (cachedCol || this.getDefaultCol());
    }

    this.applyColumnView(colToUse);

    addEventDelegate({
      selector: this.selectors.cols[0],
      context: this.domNodes.toolbar,
      handler: (e, colNode) => this.applyColumnView(Number(colNode.dataset.column)),
    });
    this.initLoadMore();
    if (!this.sideEffectEventsAdded) {
      window.addEventListener("resize", debounce(this.handleResize, 300));
    }
  };

  handleResize = () => {
    const storageKey = this.getStorageKey();
    let targetCol;

    if (this.isMobile()) {
      targetCol = Math.min(Math.max(Number(window.localStorage.getItem(storageKey)) || this.getDefaultCol(), 1), 2);
    } else {
      targetCol = Number(window.localStorage.getItem(storageKey)) || this.getDefaultCol();
      const colSwitcher = this.domNodes.section.querySelector('.m-toolbar--column-switcher');
      if (colSwitcher) colSwitcher.style.display = '';
    }

    if (this.activeCol !== targetCol) {
      this.applyColumnView(targetCol);
    }
    this.handleStickyToolbar();
  };

  applyColumnView = (col) => {
    if (!col || this.view === "search") return;
    const isMobile = this.isMobile();
    col = isMobile ? Math.min(Math.max(col, 1), 2) : col;
    const { cols, productContainer } = this.domNodes;
    if (!productContainer) return;

    const allImages = productContainer.querySelectorAll("responsive-image");
    if (allImages && allImages.length > 0) {
      allImages.forEach((img) => {
        img.disconnectedCallback();
        img.connectedCallback();
      });
    }

    if (this.showColSwitchers) {
      if (this.activeCol && cols[this.activeCol - 1]) {
        cols[this.activeCol - 1].classList.remove("active");
      }
      if (cols[col - 1]) {
        cols[col - 1].classList.add("active");
      }
    }

    productContainer.classList.remove("m-cols-1", "m-cols-2", "m-cols-3", "m-cols-4", "m-cols-5");
    productContainer.classList.add(`m-cols-${col}`);

    const mobileColClasses = ["lh-mobile-1col", "lh-mobile-2col", "lh-mobile-cols-1", "lh-mobile-cols-2"];
    productContainer.classList.remove(...mobileColClasses);
    if (isMobile) {
      productContainer.classList.add(col <= 1 ? "lh-mobile-cols-1" : "lh-mobile-cols-2");
    }

    window.localStorage.setItem(this.getStorageKey(), col);
    this.activeCol = col;
  };

  initFilters = () => {
    if (!this.enableFilters) return;
    const { sidebar, openSidebar, closeSidebar } = this.domNodes;
    window.requestAnimationFrame(this.initAccordions);
    openSidebar.addEventListener("click", this.openSidebarFilter);
    closeSidebar.addEventListener("click", this.closeSidebarFilter);
    sidebar.addEventListener("click", (e) => e.target === sidebar && this.closeSidebarFilter());
  };

  initMobileSorting = () => {
    if (!this.enableSorting) return;
    const { openSorting, closeSorting, sortingWrapper } = this.mbSortingNodes;
    openSorting.addEventListener("click", this.openMobileSorting);
    closeSorting.addEventListener("click", this.closeMobileSorting);
    sortingWrapper.addEventListener("click", (e) => e.target === sortingWrapper && this.closeMobileSorting());
  };

  openMobileSorting = () => {
    const { sortingWrapper, sortingList } = this.mbSortingNodes;
    sortingWrapper.style.display = "block";
    window.requestAnimationFrame(() => {
      sortingWrapper.style.setProperty("--m-bg-opacity", "0.5");
      sortingList.style.setProperty("--m-translate-y", "0");
    });
  };

  closeMobileSorting = () => {
    const { sortingWrapper, sortingList } = this.mbSortingNodes;
    sortingWrapper.style.setProperty("--m-bg-opacity", "0");
    sortingList.style.setProperty("--m-translate-y", "100%");
    setTimeout(() => sortingWrapper.style.setProperty("display", "none"), 300);
  };

  initAccordions = () => {
    this.accordions && this.accordions.destroy();
    const { filtersWrapper } = this.domNodes;
    filtersWrapper.classList.remove("acc-initialized");
    this.accordions = new LhLibs.Accordion(filtersWrapper, {
      presetContentHeight: window.innerWidth > 1280 && this.filtersPosition !== "fixed",
      callback: () => filtersWrapper.style.setProperty('opacity', '1')
    });
  };

  openSidebarFilter = () => {
    const { sidebar, sidebarContent, section } = this.domNodes;
    sidebar.style.display = "block";
    window.requestAnimationFrame(() => {
      sidebar.style.setProperty("--m-bg-opacity", "0.5");
      sidebarContent.style.setProperty("--m-translate-x", "0");
      this.accordions && this.accordions.setContentHeight();
    });
    section.classList.add("sidebar-open");
  };

  closeSidebarFilter = () => {
    const { sidebar, sidebarContent, section } = this.domNodes;
    section.classList.remove("sidebar-open");
    if (window.innerWidth < 1280 || sidebar.dataset.type === "fixed") {
      sidebarContent.style.setProperty("--m-translate-x", "-100%");
      sidebar.style.removeProperty("--m-bg-opacity");
      setTimeout(() => sidebar.style.removeProperty("display"), 300);
    }
  };

  initLoadMore = () => {
    if (this.paginationType === "paginate" || this.totalPages <= 1 || this.view === "search") return;
    const { loadMoreBtn } = this.domNodes;
    loadMoreBtn.addEventListener("click", this.loadMoreProducts);
    if (this.paginationType === "infinite") {
      this.infiniteLoadingObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio === 1) this.loadMoreProducts();
          });
        },
        { threshold: 1 }
      );
      this.infiniteLoadingObserver.observe(loadMoreBtn);
    }
  };

  loadMoreProducts = () => {
    const nextPage = this.currentPage + 1;
    if (nextPage > this.totalPages) return;
    const { productContainer, loadMoreBtn } = this.domNodes;
    this.toggleLoading(true);
    fetchSection(this.sectionId, { fromCache: true, params: { page: nextPage } })
      .then((productGridHTML) => {
        const productNodes = productGridHTML.querySelectorAll("[data-product-container] .m-product-item");
        productNodes.forEach((prodNode) => productContainer.appendChild(prodNode));
        this.initProducts();
      })
      .catch((err) => console.error(`Failed to load more products.`, err))
      .finally(() => {
        LhEvents.emit("ON_PRODUCT_LIST_UPDATED");
        this.toggleLoading(false);
        this.currentPage = nextPage;
        if (nextPage >= this.totalPages) {
          loadMoreBtn.parentNode.remove();
          this.infiniteLoadingObserver && this.infiniteLoadingObserver.unobserve(loadMoreBtn);
        }
      });
  };

  initProducts = () => {
    LhTheme.CompareProduct && LhTheme.CompareProduct.setCompareButtonsState();
    LhTheme.Wishlist && LhTheme.Wishlist.setWishlistButtonsState();
  };

  toggleLoading = (loading) => {
    const { loadMoreBtn } = this.domNodes;
    if (loadMoreBtn) {
      loadMoreBtn.classList[loading ? "add" : "remove"]("m-spinner-loading");
    }
  };

  handleStickyToolbar() {
    const toolbar = this.domNodes.toolbar;
    if (!toolbar) return;
    const extraSpace = toolbar.offsetTop;
    let lastScroll = window.scrollY;
    const handler = () => {
      if (!LhTheme.config.mqlMobile) {
        toolbar.classList.remove("scroll-up", "m-collection-toolbar--sticky", "scroll-down");
        return;
      }
      const currentScroll = window.scrollY;
      if (currentScroll <= extraSpace) {
        toolbar.classList.remove("scroll-up", "m-collection-toolbar--sticky");
        return;
      }
      toolbar.classList.add("m-collection-toolbar--sticky");
      if (currentScroll > toolbar.offsetHeight + extraSpace && currentScroll > lastScroll && !toolbar.classList.contains("scroll-down")) {
        toolbar.classList.remove("scroll-up");
        toolbar.classList.add("scroll-down");
      } else if (currentScroll < lastScroll && toolbar.classList.contains("scroll-down")) {
        toolbar.classList.remove("scroll-down");
        toolbar.classList.add("scroll-up");
      }
      lastScroll = currentScroll;
    };
    if (!this._stickyHandlerBound) {
      window.addEventListener("scroll", handler, { passive: true });
      this._stickyHandlerBound = handler;
    }
  }
}

LhTheme = LhTheme || {};
LhTheme.Collection = new Collection();

(function initCollection() {
  const init = () => {
    if (document.querySelector(".facest-filters-section")) {
      LhTheme.Collection.init();
    }
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener("shopify:section:load", init);
  }
})();
